import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IFieldValue, IRegistrationField, MULTIVALUE_TYPES } from '../../registration-form.service';

export interface ConditionEntry {
  field: IRegistrationField;
  value: IFieldValue;
}

export type ConditionPendingOp =
  | { kind: 'add'; valueId: number; questionIds: number[] }
  | { kind: 'remove'; valueId: number; questionIds: number[] };

/** Stand-in for the option_id of a field that hasn't been saved yet */
export const NEW_FIELD_PLACEHOLDER_ID = -1;

@Component({
  selector: 'edit-registration-field-conditions',
  templateUrl: './edit-registration-field-conditions.component.html',
  styleUrls: ['./edit-registration-field-conditions.component.scss'],
})
export class EditRegistrationFieldConditionsComponent implements OnInit, OnChanges {
  @Input() field: IRegistrationField | null = null;
  @Input() allFields: IRegistrationField[] = [];
  @Output() pendingOpsChange = new EventEmitter<ConditionPendingOp[]>();

  public conditions: ConditionEntry[] = [];
  public pendingOps: ConditionPendingOp[] = [];

  // Add condition modal
  public showAddModal = false;
  public modalFieldId: number | null = null;
  public modalValueId: number | null = null;

  ngOnInit() {
    this.conditions = this.computeConditions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.field) {
      this.pendingOps = [];
      this.pendingOpsChange.emit(this.pendingOps);
    }
    if (changes.field || changes.allFields) {
      this.conditions = this.computeConditions();
    }
  }

  /** option_id of the current field, or a placeholder while it hasn't been saved yet */
  private get currentFieldId(): number {
    return this.field ? this.field.option_id : NEW_FIELD_PLACEHOLDER_ID;
  }

  private computeConditions(): ConditionEntry[] {
    const currentId = this.currentFieldId;
    const entries: ConditionEntry[] = [];
    for (const f of this.allFields) {
      if (!f.values) continue;
      for (const v of f.values) {
        if (this.effectiveQtd(v).indexOf(currentId) >= 0) {
          entries.push({ field: f, value: v });
        }
      }
    }
    return entries;
  }

  /** Returns questions_to_display for a value, taking pending ops into account */
  effectiveQtd(value: IFieldValue): number[] {
    const base = value.questions_to_display ? [...value.questions_to_display] : [];
    const addOp = this.pendingOps.find(op => op.kind === 'add' && op.valueId === value.option_value_id);
    const removeOp = this.pendingOps.find(op => op.kind === 'remove' && op.valueId === value.option_value_id);
    if (addOp) return addOp.questionIds;
    if (removeOp) return removeOp.questionIds;
    return base;
  }

  removeCondition(entry: ConditionEntry) {
    const currentId = this.currentFieldId;
    const origQtd = entry.value.questions_to_display ? [...entry.value.questions_to_display] : [];
    const newQtd = origQtd.filter(id => id !== currentId);

    // Replace or add a 'remove' pending op for this value
    this.pendingOps = [
      ...this.pendingOps.filter(op => op.valueId !== entry.value.option_value_id),
      { kind: 'remove', valueId: entry.value.option_value_id, questionIds: newQtd },
    ];
    this.pendingOpsChange.emit(this.pendingOps);
    this.conditions = this.computeConditions();
}

  // Add condition modal

  get modalField(): IRegistrationField | null {
    if (this.modalFieldId == null) return null;
    return this.allFields.find(f => f.option_id === this.modalFieldId) || null;
  }

  get modalFieldValues(): IFieldValue[] {
    return (this.modalField && this.modalField.values) ? this.modalField.values : [];
  }

  /** Selectable fields: multivalue fields, not the current field, and not unused default fields */
  get selectableFields(): IRegistrationField[] {
    const field = this.field;
    return this.allFields.filter(f =>
      (!field || f.option_id !== field.option_id)
      && MULTIVALUE_TYPES.indexOf(f.field_type) >= 0
      && !(f.is_default && f.is_in_use === false)
    );
  }

  onModalFieldChange() {
    this.modalValueId = null;
  }

  openAddModal() {
    this.modalFieldId = null;
    this.modalValueId = null;
    this.showAddModal = true;
  }

  closeAddModal() {
    this.showAddModal = false;
  }

  saveCondition() {
    if (this.modalValueId == null) return;
    const currentId = this.currentFieldId;
    const value = this.modalFieldValues.find(v => v.option_value_id === this.modalValueId);
    if (!value) return;

    const origQtd = value.questions_to_display ? [...value.questions_to_display] : [];
    if (origQtd.indexOf(currentId) >= 0) {
      this.closeAddModal();
      return; // already present
    }
    const newQtd = [...origQtd, currentId];

    this.pendingOps = [
      ...this.pendingOps.filter(op => op.valueId !== value.option_value_id),
      { kind: 'add', valueId: value.option_value_id, questionIds: newQtd },
    ];
    this.pendingOpsChange.emit(this.pendingOps);
    this.closeAddModal();
    this.conditions = this.computeConditions();
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/registration-form/edit-registration-field-conditions/edit-registration-field-conditions.component.ts